'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { motion } from 'framer-motion';
import { Copyright, CornerDownRight, Menu } from 'lucide-react';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import { SERVICE_ITEMS } from '@/constants/constants';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import Popover from '@/components/modal/Popover';
import HeaderSlideMenu from '@/components/modal/features/HeaderSlideMenu';
import UserAvatar from '@/components/ui/avatar/UserAvatar';

export default function PcHeader() {
    const { safePush } = useSafeNavigation();
    const { scheme } = useThemeColor();
    const userAvatarUrl = useAuthStore((s) => s.userAvatarUrl);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const schoomyersId = useAuthStore((state) => state.userAuth?.schoomyersId);
    const pathname = usePathname();
    const openModalSlide = useModalSlideStore((state) => state.openModalSlide);

    const [activeSubItemsPopover, setActiveSubItemsPopover] = useState<{
        label: string;
        subItems: {
            label: string;
            jp: string;
            Icon: any;
            items: { label: string; jp: string; path: string }[];
        }[];
        position: { top: number; left: number };
    } | null>(null);

    const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const setItemRef = useCallback(
        (label: string) => (el: HTMLDivElement | null) => {
            itemRefs.current[label] = el;
        },
        [],
    );

    useEffect(() => {
        if (!activeSubItemsPopover) return;

        const handleResize = () => {
            const el = itemRefs.current[activeSubItemsPopover.label];
            if (el) {
                const rect = el.getBoundingClientRect();
                setActiveSubItemsPopover((prev) =>
                    prev
                        ? {
                              ...prev,
                              position: {
                                  top: rect.bottom + window.scrollY,
                                  left: rect.left + rect.width / 2,
                              },
                          }
                        : null,
                );
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeSubItemsPopover]);

    return (
        <>
            <div
                className="fixed flex flex-col justify-start items-center bg-color-white-base w-full z-30 shadow-bottom overflow-x-hidden overflow-y-visible"
                style={{
                    paddingTop: 'env(safe-area-inset-top)',
                    height: 'calc(4rem + env(safe-area-inset-top))',
                }}
            >
                <motion.header
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0 }}
                    className="flex flex-grow justify-center items-center w-full h-full select-none"
                >
                    <div className="flex w-full h-full bg-color-white-base">
                        <div className="w-[5%]" />
                        <div className="flex flex-grow justify-start items-center select-none">
                            <div className="flex items-end cursor-pointer" onClick={() => safePush('/')}>
                                <div className="font-nova font-bold text-color-base text-[40px] pl-2 h-16">fox.</div>
                                <div className="flex items-center h-16">
                                    <div className="font-montserrat font-black text-color-base text-xs pt-4">
                                        <IconAdaptive
                                            Icon={Copyright}
                                            strokeWidth={2.25}
                                            baseSize={12}
                                            className="text-color-base"
                                        />
                                    </div>
                                    <div className="ml-[3px] pt-3">
                                        <ImageAdaptive src="/images/logo/schoomy.svg" baseWidth={110} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-grow justify-end items-center h-full">
                            {SERVICE_ITEMS.map(({ label, jp, path, match, Icon, subItems }) => {
                                const isActive = match.some((p) => pathname.startsWith(p));
                                const hasSubItems = subItems.length > 0;

                                return (
                                    <div key={path} className="flex">
                                        <div
                                            ref={setItemRef(label)}
                                            className="relative flex flex-col items-end cursor-pointer group"
                                            onClick={() => {
                                                if (hasSubItems) {
                                                    const el = itemRefs.current[label];
                                                    if (!el) return;
                                                    const rect = el.getBoundingClientRect();
                                                    setActiveSubItemsPopover({
                                                        label,
                                                        subItems,
                                                        position: {
                                                            top: rect.bottom + window.scrollY,
                                                            left: rect.left + rect.width / 2,
                                                        },
                                                    });
                                                } else {
                                                    safePush(path);
                                                }
                                            }}
                                        >
                                            <div className="flex">
                                                <motion.div
                                                    className={`flex pr-0 lg:pr-3 mt-[3px] transition-opacity duration-150 ${
                                                        isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                                    }`}
                                                >
                                                    <IconAdaptive
                                                        Icon={CornerDownRight}
                                                        strokeWidth={2.25}
                                                        baseSize={18}
                                                        className={`${scheme?.text}`}
                                                    />
                                                </motion.div>
                                                <div>
                                                    <Icon
                                                        className={`transition-colors duration-200 ${
                                                            isActive ? scheme?.text : 'text-color-base-layout-logo'
                                                        }`}
                                                        strokeWidth={2.25}
                                                        size={24}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex mt-1">
                                                <span className="sr-only">{`${label} ${jp}`}</span>
                                                <span
                                                    aria-hidden="true"
                                                    className="font-montserrat font-black text-sm pr-1 text-color-base-layout-logo hidden lg:block"
                                                >
                                                    {label}
                                                </span>
                                                <span
                                                    aria-hidden="true"
                                                    className="font-corp font-bold text-xs text-color-base-layout-logo-jp pt-[2px] hidden lg:block"
                                                >
                                                    {jp}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mr-[50px]" />
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex items-center justify-center">
                            <motion.div
                                initial={false}
                                whileHover={{ y: 2 }}
                                className="p-2 rounded cursor-pointer group relative"
                                onClick={() =>
                                    openModalSlide({
                                        id: 'header-menu-pc',
                                        isHeaderLog: true,
                                        component: <HeaderSlideMenu />,
                                        size: { pcWidth: '100%', mbWidth: '100%', height: '100%' },
                                        direction: 'right',
                                    })
                                }
                            >
                                {isAuthenticated ? (
                                    <>
                                        <UserAvatar
                                            schoomyers_id={Number(schoomyersId)}
                                            avatarUrl={userAvatarUrl ?? undefined}
                                            size={40}
                                            borderColorClassName="border-color-base"
                                        />
                                        <motion.div
                                            className="absolute top-[4px] right-[2px]"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 1.8, duration: 0.5 }}
                                        >
                                            <ImageAdaptive
                                                src="/images/common/online.svg"
                                                baseWidth={20}
                                                breakpoints={{ md: 20 }}
                                            />
                                        </motion.div>
                                    </>
                                ) : (
                                    <>
                                        <IconAdaptive
                                            Icon={Menu}
                                            strokeWidth={1.5}
                                            baseSize={34}
                                            className="text-color-base group-hover:text-color-service-mouseover"
                                        />
                                        <motion.div
                                            className="absolute top-[4px] right-[2px]"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 1.8, duration: 0.5 }}
                                        >
                                            <ImageAdaptive
                                                src="/images/common/offline.svg"
                                                baseWidth={20}
                                                breakpoints={{ md: 20 }}
                                            />
                                        </motion.div>
                                    </>
                                )}
                            </motion.div>
                        </div>
                        <div className="w-[5%]" />
                    </div>
                </motion.header>
            </div>

            {activeSubItemsPopover && (
                <Popover
                    label={activeSubItemsPopover.label}
                    sections={activeSubItemsPopover.subItems}
                    direction="down"
                    position={activeSubItemsPopover.position}
                    onNavigate={(path) => {
                        safePush(path);
                        setActiveSubItemsPopover(null);
                    }}
                    onClose={() => setActiveSubItemsPopover(null)}
                />
            )}
        </>
    );
}
